import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import {
  ArrowLeft,
  Calendar,
  Search,
  UserPlus,
  Package,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileText,
  MapPin,
  ShieldCheck,
  StickyNote,
  Check,
  Edit2,
  X,
  ChevronRight
} from 'lucide-react';

// Modal Imports
import ItemSelectModal from './ItemSelectModal';
import QuickAddItemModal from './QuickAddItemModal';
import CustomFieldsModal from './CustomFieldsModal';
import PartyAddressModal from './PartyAddressModal';
import YourAddressModal from './YourAddressModal';
import TermsConditionsModal from './TermsConditionsModal';
import AddNotesModal from './AddNotesModal';
import PartySelectModal from './PartySelectModal';
import AddPartyModal from '../parties/AddPartyModal';
import billService from '../../services/billService';
import EditPriceModal from './EditPriceModal';
import { addEntry } from '../../services/dashboardApi';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear().toString().substring(2)}`;
};

const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const pad = (num) => String(num).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};


const JaggedDivider = () => (
  <div style={styles.jaggedContainer}>
    <svg width="100%" height="10" viewBox="0 0 100 10" preserveAspectRatio="none">
      <path d="M0 10 L5 0 L10 10 L15 0 L20 10 L25 0 L30 10 L35 0 L40 10 L45 0 L50 10 L55 0 L60 10 L65 0 L70 10 L75 0 L80 10 L85 0 L90 10 L95 0 L100 10"
        fill="none" stroke="#E2E8F0" strokeWidth="1" />
    </svg>
  </div>
);

const AddBillView = ({ type, profile, isGst, onBack, onSaved, initialBill = null, initialBillNumber = "1" }) => {
  const { t, lang } = useLang();

  // --- STATE ---
  const [extraDiscountValue, setExtraDiscountValue] = useState(0);
  const [extraDiscountType, setExtraDiscountType] = useState('rupee');

  const [billNumber, setBillNumber] = useState(initialBill?.billNumber || initialBillNumber);
  const [selectedDate, setSelectedDate] = useState(initialBill?.date || new Date());
  const [selectedCustomer, setSelectedCustomer] = useState(initialBill?.customer || null);
  const [billItems, setBillItems] = useState(initialBill?.items || []);
  const [paymentType, setPaymentType] = useState(initialBill?.paymentType || 'unpaid');
  const [roundOff, setRoundOff] = useState(!!initialBill?.roundOffAmount);
  const [showOptional, setShowOptional] = useState(false);
  const [notes, setNotes] = useState(initialBill?.privateNotes || "");
  const [photos, setPhotos] = useState(initialBill?.photoUrls || []);
  const [addToLedger, setAddToLedger] = useState(false);

  // Additional Optional Fields State
  const [customFields, setCustomFields] = useState(initialBill?.customFields || []);
  const [partyAddress, setPartyAddress] = useState(initialBill?.customer?.address || null);
  const [yourAddress, setYourAddress] = useState(initialBill?.yourAddress || null);
  const [terms, setTerms] = useState(initialBill?.termsAndConditions || "");

  // Modal Visibility State
  const [activeModal, setActiveModal] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (initialBill) {
      setBillNumber(initialBill.billNumber || initialBill.bill_number || '');
      setSelectedDate(new Date(initialBill.date || initialBill.bill_date));
      setSelectedCustomer(initialBill.customer);
      setBillItems(initialBill.items || []);
      setPartyAddress(initialBill.partyAddress || initialBill.party_address);
      setYourAddress(initialBill.yourAddress || initialBill.your_address);
      setTerms(initialBill.termsAndConditions || initialBill.terms_n_conditions || '');
      setNotes(initialBill.privateNotes || initialBill.private_note || '');
      setPhotos(initialBill.photoUrls || initialBill.photos || []);
      setPaymentType(initialBill.paymentType || initialBill.payment_mode || 'unpaid');
      setRoundOff(!!initialBill.roundOffAmount || !!initialBill.round_off_amount);
      setCustomFields(initialBill.custom_fields ? (typeof initialBill.custom_fields === 'string' ? JSON.parse(initialBill.custom_fields) : initialBill.custom_fields) : []);
      setAddToLedger(initialBill.addedToLedger === true || initialBill.added_to_ledger === 1 || initialBill.added_to_ledger === '1' || initialBill.added_to_ledger === true);

      const itemDiscountsSum = (initialBill.items || []).reduce((sum, item) => {
        const q = parseFloat(item.quantity || item.qty || 1);
        const disc = parseFloat(item.discountValue || item.discount_value || 0);
        return sum + (disc * q);
      }, 0);
      const totalDisc = initialBill.totalDiscount || initialBill.total_discount || 0;
      const diff = totalDisc - itemDiscountsSum;
      setExtraDiscountValue(diff > 0.01 ? parseFloat(diff.toFixed(2)) : 0);
      setExtraDiscountType('rupee');
    } else {
      setBillNumber(initialBillNumber || '');
      setSelectedDate(new Date());
      setSelectedCustomer(null);
      setBillItems([]);
      setPartyAddress(null);
      setYourAddress(null);
      setTerms('');
      setNotes('');
      setPhotos([]);
      setPaymentType('unpaid');
      setRoundOff(false);
      setCustomFields([]);
      setAddToLedger(false);
      setExtraDiscountValue(0);
      setExtraDiscountType('rupee');
    }
  }, [initialBill, initialBillNumber]);

  // --- CALCULATIONS ---
  const subtotal = useMemo(() => {
    return billItems.reduce((sum, item) => sum + (item.quantity * item.originalPrice), 0);
  }, [billItems]);

  const itemDiscountsSum = useMemo(() => {
    return billItems.reduce((sum, item) => sum + (item.quantity * (item.discountValue || 0)), 0);
  }, [billItems]);

  const extraDiscount = useMemo(() => {
    if (extraDiscountType === 'percentage') {
      return (subtotal * extraDiscountValue) / 100;
    }
    return extraDiscountValue;
  }, [subtotal, extraDiscountValue, extraDiscountType]);

  const totalDiscount = useMemo(() => {
    return itemDiscountsSum + (parseFloat(extraDiscount) || 0);
  }, [itemDiscountsSum, extraDiscount]);

  const displaySubtotal = useMemo(() => {
    return billItems.reduce((sum, item) => {
      const rawGst = item.gstRate || item.item?.gstRate || item.item?.gst_rate || item.gst_rate || "0";
      const gstRateString = rawGst.toString().replace('%', '').trim();
      const rate = parseFloat(gstRateString) || 0;
      const taxInc = item.taxIncluded || item.item?.taxIncluded || false;
      const price = isGst && taxInc ? item.originalPrice * (1 + rate / 100) : item.originalPrice;
      return sum + (item.quantity * price);
    }, 0);
  }, [billItems, isGst]);

  const displayTotalDiscount = useMemo(() => {
    const itemDisplayDiscount = billItems.reduce((sum, item) => {
      const rawGst = item.gstRate || item.item?.gstRate || item.item?.gst_rate || item.gst_rate || "0";
      const gstRateString = rawGst.toString().replace('%', '').trim();
      const rate = parseFloat(gstRateString) || 0;
      const taxInc = item.taxIncluded || item.item?.taxIncluded || false;
      const discount = isGst && taxInc ? (item.discountValue || 0) * (1 + rate / 100) : (item.discountValue || 0);
      return sum + (item.quantity * discount);
    }, 0);
    return itemDisplayDiscount + (parseFloat(extraDiscount) || 0);
  }, [billItems, isGst, extraDiscount]);

  const taxDetailsSplit = useMemo(() => {
    let cgstIncluded = 0, sgstIncluded = 0, igstIncluded = 0;
    let cgstAdded = 0, sgstAdded = 0, igstAdded = 0;

    if (!isGst || billItems.length === 0) {
      return { cgstIncluded, sgstIncluded, igstIncluded, cgstAdded, sgstAdded, igstAdded };
    }

    const bizState = (profile?.businessState || profile?.state || '').toLowerCase().trim();
    let custState = '';
    if (typeof partyAddress === 'object' && partyAddress?.state) {
      custState = partyAddress.state.toLowerCase().trim();
    } else if (typeof partyAddress === 'string' && !partyAddress.startsWith('{')) {
      const parts = partyAddress.split(',').map(p => p.trim().toLowerCase());
      if (parts.length > 1) custState = parts[parts.length - 2];
    }
    const isInterState = bizState && custState && bizState !== custState;

    billItems.forEach(item => {
      const taxable = (item.quantity * item.originalPrice) - (item.quantity * (item.discountValue || 0));
      const rawGst = item.gstRate || item.item?.gstRate || item.item?.gst_rate || item.gst_rate || "0";
      const gstRateString = rawGst.toString().replace('%', '').trim();
      const rate = parseFloat(gstRateString) || 0;
      const tax = (taxable * rate) / 100;
      const taxInc = item.taxIncluded || item.item?.taxIncluded || false;

      if (taxInc) {
        if (isInterState) {
          igstIncluded += tax;
        } else {
          cgstIncluded += tax / 2;
          sgstIncluded += tax / 2;
        }
      } else {
        if (isInterState) {
          igstAdded += tax;
        } else {
          cgstAdded += tax / 2;
          sgstAdded += tax / 2;
        }
      }
    });

    return { cgstIncluded, sgstIncluded, igstIncluded, cgstAdded, sgstAdded, igstAdded };
  }, [billItems, isGst, profile, partyAddress]);

  const taxDetails = useMemo(() => {
    let cgst = 0, sgst = 0, igst = 0;
    if (!isGst || billItems.length === 0) return { cgst, sgst, igst, total: 0 };

    // Get Business State (from profile)
    const bizState = (profile?.businessState || profile?.state || '').toLowerCase().trim();

    // Get Customer State (from partyAddress object or selectedCustomer)
    let custState = '';
    if (typeof partyAddress === 'object' && partyAddress?.state) {
      custState = partyAddress.state.toLowerCase().trim();
    } else if (typeof partyAddress === 'string') {
      // Try to extract state from string if it's not JSON
      if (!partyAddress.startsWith('{')) {
        const parts = partyAddress.split(',').map(p => p.trim().toLowerCase());
        // Heuristic: check last or second to last part
        if (parts.length > 1) custState = parts[parts.length - 2];
      }
    }

    const isInterState = bizState && custState && bizState !== custState;

    billItems.forEach(item => {
      const taxable = (item.quantity * item.originalPrice) - (item.quantity * (item.discountValue || 0));

      // Improved parsing matching Flutter reference and handling multiple field names
      const rawGst = item.gstRate || item.item?.gstRate || item.item?.gst_rate || item.gst_rate || "0";
      const gstRateString = rawGst.toString().replace('%', '').trim();
      const rate = parseFloat(gstRateString) || 0;

      const tax = (taxable * rate) / 100;

      // In the flutter reference folder, it currently splits 50/50 for CGST/SGST
      // unless it's explicitly inter-state. We'll keep the inter-state logic
      // but ensure intra-state is the robust default.
      if (isInterState) {
        igst += tax;
      } else {
        cgst += tax / 2;
        sgst += tax / 2;
      }
    });

    return { cgst, sgst, igst, total: cgst + sgst + igst };
  }, [billItems, isGst, profile, partyAddress]);

  const finalAmount = useMemo(() => {
    let total = subtotal - totalDiscount + taxDetails.total;
    if (roundOff) total = Math.round(total);
    return total;
  }, [subtotal, totalDiscount, taxDetails, roundOff]);

  const roundOffAmount = useMemo(() => {
    if (!roundOff) return 0;
    const total = subtotal - totalDiscount + taxDetails.total;
    return Math.round(total) - total;
  }, [subtotal, totalDiscount, taxDetails, roundOff]);

  const balanceDue = paymentType === 'unpaid' ? finalAmount : 0;

  // --- ACTIONS ---
  const handleRemoveItem = (index) => {
    const newItems = [...billItems];
    newItems.splice(index, 1);
    setBillItems(newItems);
  };

  const handleSavePrice = (updatedItem) => {
    const newItems = [...billItems];
    newItems[editingItemIndex] = updatedItem;
    setBillItems(newItems);
    setEditingItemIndex(null);
  };



  const handleSubmit = async () => {
    if (isSaving) return;
    if (!selectedCustomer) {
      alert(type === 'purchase' ? t(LangKeys.add_bill_select_supplier_error) : t(LangKeys.add_bill_select_customer_error));
      return;
    }
    if (billItems.length === 0) {
      alert(t(LangKeys.add_bill_add_item_error));
      return;
    }

    // Recalculate isInterState for payload mapping
    const bizState = (profile?.businessState || profile?.state || '').toLowerCase().trim();
    let custState = '';
    if (typeof partyAddress === 'object' && partyAddress?.state) {
      custState = partyAddress.state.toLowerCase().trim();
    } else if (typeof partyAddress === 'string' && !partyAddress.startsWith('{')) {
      const parts = partyAddress.split(',').map(p => p.trim().toLowerCase());
      if (parts.length > 1) custState = parts[parts.length - 2];
    }
    const isInterState = bizState && custState && bizState !== custState;

    setIsSaving(true);
    try {
      const apiPayload = {
        id: initialBill?.id,
        bill_type: type,
        is_gst: isGst ? 1 : 0,
        bill_number: billNumber,
        bill_date: formatLocalDate(selectedDate),
        party_id: selectedCustomer?.id,
        party_type: selectedCustomer?.isSupplier ? 'supplier' : 'customer',
        customer_name: selectedCustomer?.customerName,
        customer_mobile: selectedCustomer?.mobileNo,
        customer_gstin: selectedCustomer?.gstin,
        bill_items: billItems.map(item => {
          const taxable = (item.quantity * item.originalPrice) - (item.quantity * (item.discountValue || 0));
          const rate = parseFloat(item.item?.gstRate?.replace('%', '') || item.gstRate || 0);
          const tax = isGst ? (taxable * rate) / 100 : 0;
          const itemCgst = isGst && !isInterState ? tax / 2 : 0;
          const itemSgst = isGst && !isInterState ? tax / 2 : 0;
          const itemIgst = isGst && isInterState ? tax : 0;

          return {
            item_id: item.item?.id || item.id,
            item_name: item.item?.name || item.name,
            hsn_sac_code: item.item?.hsnSacCode || '',
            quantity: item.quantity || 1,
            unit_price: item.price || 0,
            original_price: item.originalPrice || 0,
            discount_value: item.discountValue || 0,
            discount_type: item.discountType || 'rupee',
            tax_amount: tax,
            tax_rate: rate,
            gst_rate: `${rate}%`,
            gstRate: `${rate}%`,
            total: taxable + tax,
            cgst: itemCgst,
            sgst: itemSgst,
            igst: itemIgst,
            tax_included: item.taxIncluded ? 1 : 0
          };
        }),
        subtotal: subtotal,
        total_discount: totalDiscount,
        extra_discount: extraDiscount,
        total_tax: taxDetails.total,
        cgst: taxDetails.cgst,
        sgst: taxDetails.sgst,
        igst: taxDetails.igst,
        round_off_amount: roundOffAmount || 0,
        total_bill_amount: finalAmount,
        final_bill_amount: finalAmount,
        payment_mode: paymentType,
        payment_type: paymentType,
        terms_n_conditions: terms,
        private_note: notes,
        party_address: typeof partyAddress === 'object' && partyAddress !== null ? JSON.stringify(partyAddress) : partyAddress,
        your_address: typeof yourAddress === 'object' && yourAddress !== null ? JSON.stringify(yourAddress) : yourAddress,
        custom_fields: customFields?.length > 0 ? JSON.stringify(customFields) : null,
        added_to_ledger: addToLedger ? 1 : 0,
        items: billItems.map(item => ({
          item_id: item.item?.id || item.id,
          item_name: item.item?.name || item.name,
          quantity: item.quantity || 1
        }))
      };

      // Real Save Logic (Parity with Mobile)
      let result;
      if (initialBill?.id) {
        result = await billService.updateBill(initialBill.id, apiPayload);
      } else {
        result = await billService.addBill(apiPayload);
      }

      if (result.success) {
        // Merge server response with local payload to ensure all fields are present
        const combinedData = { ...apiPayload, ...(result.data || result.bill || {}) };
        const normalizedBill = billService._normalizeBill(combinedData);

        // Map server-returned bill_id to guarantee valid ID is present on first preview
        const serverBillId = result.bill_id || result.data?.id || result.bill?.id || combinedData.id;
        if (serverBillId) {
          normalizedBill.id = serverBillId.toString();
        }

        // Upload new photos to the server
        const newPhotos = photos.filter(p => p && p.file);
        if (newPhotos.length > 0 && serverBillId) {
          for (const p of newPhotos) {
            try {
              await billService.uploadBillPhoto(p.file, serverBillId);
            } catch (uploadErr) {
              console.error("Failed to upload photo:", uploadErr);
            }
          }
          try {
            const updatedBill = await billService.getBillDetails(serverBillId);
            if (updatedBill) {
              normalizedBill.photoUrls = updatedBill.photoUrls;
            }
          } catch (refetchErr) {
            console.error("Error refetching bill details after photo uploads:", refetchErr);
          }
        }
        
        // Ensure customer details and addresses are fully populated from local state on save
        normalizedBill.customer = {
          ...normalizedBill.customer,
          customerName: selectedCustomer?.customerName || normalizedBill.customer?.customerName,
          mobileNo: selectedCustomer?.mobileNo || normalizedBill.customer?.mobileNo,
          gstin: selectedCustomer?.gstin || normalizedBill.customer?.gstin,
          address: partyAddress || normalizedBill.customer?.address
        };
        normalizedBill.partyAddress = partyAddress || normalizedBill.partyAddress;
        normalizedBill.yourAddress = yourAddress || normalizedBill.yourAddress;

        // Post ledger entry if payment is unpaid and user requested to add due balance to ledger
        if (paymentType === 'unpaid' && addToLedger && (!initialBill || !initialBill.addedToLedger)) {
          try {
            const formData = new FormData();
            formData.append('customer_id', selectedCustomer.id.toString());
            formData.append('entry_type', type === 'sale' ? 'give' : 'get');
            formData.append('amount', finalAmount.toString());
            formData.append('note', `${t(LangKeys.pending_bill_no)} #${billNumber}`);
            
            // Use date from selectedDate, but current time to prevent stale times from component mounting
            const datePart = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
            const timePart = new Date(); // current instant
            
            const yyyy = datePart.getFullYear();
            const mm = String(datePart.getMonth() + 1).padStart(2, '0');
            const dd = String(datePart.getDate()).padStart(2, '0');
            
            const hh = String(timePart.getHours()).padStart(2, '0');
            const min = String(timePart.getMinutes()).padStart(2, '0');
            const ss = String(timePart.getSeconds()).padStart(2, '0');
            
            const formattedDate = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
            
            formData.append('entry_date', formattedDate);
            formData.append('date', formattedDate);
            formData.append('time', `${hh}:${min}:${ss}`);

            const entryResult = await addEntry(formData);
            if (!entryResult.success) {
              console.error("Failed to add ledger entry:", entryResult.message);
            }
          } catch (entryError) {
            console.error("Error creating ledger entry:", entryError);
          }
        }

        if (onSaved) onSaved(normalizedBill);
      } else {
        alert(result.message || "Failed to save bill");
      }
    } catch (error) {
      console.error("Save Bill Error:", error);
      alert("Error: " + (error.message || "Something went wrong"));
    } finally {
      setIsSaving(false);
    }
  };

  const getGenerateButtonText = () => {
    const isEditing = !!initialBill;
    const amountStr = formatCurrency(finalAmount);

    if (type === 'sale') {
      return isEditing
        ? `${t(LangKeys.add_bill_update_sale_bill)} ${amountStr}`
        : `${t(LangKeys.add_bill_generate_sale_bill)} ${amountStr}`;
    } else if (type === 'purchase') {
      return isEditing
        ? `${t(LangKeys.add_bill_update_purchase_bill)} ${amountStr}`
        : `${t(LangKeys.add_bill_generate_purchase_bill)} ${amountStr}`;
    } else {
      return isEditing
        ? `${t(LangKeys.add_bill_update_expense)} ${amountStr}`
        : `${t(LangKeys.add_bill_generate_expense)} ${amountStr}`;
    }
  };

  const getTitle = () => {
    const isEditing = !!initialBill;
    if (isGst) {
      if (type === 'sale') return isEditing ? t(LangKeys.add_bill_edit_sale_gst_bill) : t(LangKeys.add_bill_sale_gst_bill);
      if (type === 'purchase') return isEditing ? t(LangKeys.add_bill_edit_purchase_gst_bill) : t(LangKeys.add_bill_purchase_gst_bill);
      return isEditing ? t(LangKeys.add_bill_edit_expense) : t(LangKeys.add_bill_expense_title);
    }
    if (type === 'sale') return isEditing ? t(LangKeys.add_bill_edit_sale_bill) : t(LangKeys.add_bill_sale_bill);
    return isEditing ? t(LangKeys.add_bill_edit_purchase_bill) : t(LangKeys.add_bill_purchase_bill);
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} style={styles.backBtn}><ArrowLeft size={20} /></button>
          <h3 style={styles.title}>{getTitle()}</h3>
        </div>
      </div>

      <div style={styles.scrollContent}>
        {/* DATE & BILL NUMBER */}
        <div style={styles.sectionCard}>
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <span style={styles.label}>{type === 'sale' ? t(LangKeys.add_bill_sale_bill_no) : t(LangKeys.add_bill_purchase_bill_no)}</span>
              <div style={styles.inputWrapper}>
                <input style={styles.rawInput} value={billNumber} onChange={(e) => setBillNumber(e.target.value)} />
                <Edit2 size={14} color="#1D4ED8" />
              </div>
            </div>
            <div style={styles.inputGroup}>
              <span style={styles.label}>{t(LangKeys.date)}</span>
              <div style={styles.inputWrapper}>
                <Calendar size={16} color="#1D4ED8" />
                <input
                  type="date"
                  style={{ border: 'none', background: 'transparent', outline: 'none', color: '#0F172A', fontWeight: '700', fontSize: '14px', width: '100%', fontFamily: 'inherit' }}
                  value={`${new Date(selectedDate).getFullYear()}-${String(new Date(selectedDate).getMonth() + 1).padStart(2, '0')}-${String(new Date(selectedDate).getDate()).padStart(2, '0')}`}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [yr, mo, dy] = e.target.value.split('-').map(Number);
                      const current = new Date();
                      setSelectedDate(new Date(yr, mo - 1, dy, current.getHours(), current.getMinutes(), current.getSeconds()));
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* PARTY SECTION */}
        <div style={styles.sectionCard}>
          <span style={styles.label}>{t(LangKeys.add_bill_bill_to)}</span>
          {!selectedCustomer ? (
            <button style={styles.searchButton} onClick={() => setActiveModal('parties')}>
              <Search size={18} color="#94A3B8" />
              <span>{t(LangKeys.add_bill_search_parties)}</span>
            </button>
          ) : (
            <div style={styles.selectedParty}>
              <div style={styles.partyIcon}><UserPlus size={20} color="#64748B" /></div>
              <div style={styles.partyInfo}>
                <span style={styles.partyName}>{selectedCustomer.customerName}</span>
                <span style={styles.partySub}>{selectedCustomer.mobileNo}</span>
              </div>
              <button onClick={() => setSelectedCustomer(null)} style={styles.removeBtn}><X size={18} /></button>
            </div>
          )}
          <button style={styles.textActionBtn} onClick={() => setActiveModal('addParty')}>+ {t(LangKeys.add_bill_add_new_party)}</button>
        </div>

        {/* ITEMS SECTION */}
        <div style={styles.sectionCard}>
          <span style={styles.label}>{billItems.length} {t(LangKeys.add_bill_n_items)}</span>
          <div style={styles.itemsList}>
            {billItems.length === 0 ? (
              <div style={styles.emptyItems} onClick={() => setActiveModal('items')}>
                <Package size={24} color="#CBD5E1" />
                <span>{t(LangKeys.add_bill_enter_items_from_inventory)}</span>
              </div>
            ) : (
              billItems.map((item, idx) => {
                const rawGst = item.gstRate || item.item?.gstRate || item.item?.gst_rate || item.gst_rate || "0";
                const gstRateString = rawGst.toString().replace('%', '').trim();
                const rate = parseFloat(gstRateString) || 0;
                const taxInc = item.taxIncluded || item.item?.taxIncluded || false;

                const displayPrice = isGst && taxInc
                  ? item.originalPrice * (1 + (rate / 100))
                  : item.originalPrice;

                const displaySubtotal = item.quantity * displayPrice;
                const displayDiscount = isGst && taxInc
                  ? (item.discountValue || 0) * (1 + (rate / 100))
                  : (item.discountValue || 0);

                const displayTotalDiscount = item.quantity * displayDiscount;
                const hasDiscount = displayTotalDiscount > 0;
                const discountPercent = hasDiscount ? ((displayDiscount / displayPrice) * 100).toFixed(2) : 0;

                const taxable = (item.quantity * item.originalPrice) - (item.quantity * (item.discountValue || 0));
                const tax = isGst ? (taxable * rate) / 100 : 0;
                const rowTotal = taxable + tax;

                return (
                  <div key={idx} style={styles.itemRow} onClick={() => setEditingItemIndex(idx)}>
                    <div style={styles.itemMeta}>
                      <span style={styles.itemName}>{item.item?.name || item.name}</span>
                      <span style={styles.itemPrice}>
                        {item.quantity} x {formatCurrency(displayPrice)}
                        {isGst && (
                          <span style={{ marginLeft: '8px', color: '#1D4ED8', fontWeight: '700' }}>
                            (GST: {item.gstRate || item.item?.gst_rate || item.item?.gstRate || '0%'})
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={styles.itemRight}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {hasDiscount && (
                            <span style={{ fontSize: '12px', color: '#94A3B8', textDecoration: 'lineThrough' }}>
                              {formatCurrency(displaySubtotal)}
                            </span>
                          )}
                          <span style={styles.itemTotal}>{formatCurrency(rowTotal)}</span>
                        </div>
                        {hasDiscount && (
                          <span style={{ fontSize: '11px', color: '#10B981', fontWeight: '700' }}>
                            {discountPercent}% = {formatCurrency(displayTotalDiscount)} {t(LangKeys.add_bill_discount)}
                          </span>
                        )}
                      </div>
                      <button style={styles.itemDelBtn} onClick={() => handleRemoveItem(idx)}><Trash2 size={16} /></button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div style={styles.itemActions}>
            <button style={styles.outlineBtn} onClick={() => setActiveModal('items')}>{t(LangKeys.add_bill_edit_or_add_items)}</button>
            <button style={styles.textActionBtn} onClick={() => setActiveModal('quick_add')}>+ {t(LangKeys.add_bill_add_new_item)}</button>
          </div>
        </div>

        {/* SUMMARY SECTION */}
        <div style={styles.sectionCard}>
          <div style={styles.summaryList}>
            <div style={styles.summaryRow}><span>{t(LangKeys.add_bill_item_subtotal)}</span><span style={{ fontWeight: '700' }}>{formatCurrency(displaySubtotal)}</span></div>
            {(displayTotalDiscount - extraDiscount) > 0 && (
              <div style={styles.summaryRow}>
                <span>{t(LangKeys.add_bill_discount)}</span>
                <span style={{ color: '#EF4444' }}>- {formatCurrency(displayTotalDiscount - extraDiscount)}</span>
              </div>
            )}
            <div style={styles.summaryRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{t(LangKeys.add_bill_extra_discount)}</span>
                <select
                  value={extraDiscountType}
                  onChange={(e) => setExtraDiscountType(e.target.value)}
                  style={{
                    padding: '2px 4px',
                    fontSize: '14px',
                    border: '1px solid #CBD5E1',
                    borderRadius: '4px',
                    outline: 'none',
                    color: '#1D4ED8',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  <option value="rupee">₹</option>
                  <option value="percentage">%</option>
                </select>
                <input
                  type="number"
                  value={extraDiscountValue || ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    setExtraDiscountValue(isNaN(val) || val < 0 ? 0 : val);
                  }}
                  style={{
                    width: '80px',
                    padding: '2px 6px',
                    fontSize: '14px',
                    border: '1px solid #CBD5E1',
                    borderRadius: '4px',
                    outline: 'none',
                    textAlign: 'right',
                    color: '#1D4ED8',
                    fontWeight: '500'
                  }}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <span style={{ color: '#1D4ED8', fontWeight: '500' }}>
                {extraDiscountType === 'percentage' && `(${formatRate(extraDiscountValue)}%) `}- {formatCurrency(extraDiscount)}
              </span>
            </div>

            {isGst && (
              <>
                {(taxDetailsSplit.cgstIncluded === 0 || taxDetailsSplit.cgstAdded > 0) && (
                  <div style={styles.summaryRow}><span>CGST</span><span>{formatCurrency(taxDetailsSplit.cgstAdded)}</span></div>
                )}
                {taxDetailsSplit.cgstIncluded > 0 && (
                  <div style={styles.summaryRow}><span>CGST (Included)</span><span>{formatCurrency(taxDetailsSplit.cgstIncluded)}</span></div>
                )}
                {(taxDetailsSplit.sgstIncluded === 0 || taxDetailsSplit.sgstAdded > 0) && (
                  <div style={styles.summaryRow}><span>SGST</span><span>{formatCurrency(taxDetailsSplit.sgstAdded)}</span></div>
                )}
                {taxDetailsSplit.sgstIncluded > 0 && (
                  <div style={styles.summaryRow}><span>SGST (Included)</span><span>{formatCurrency(taxDetailsSplit.sgstIncluded)}</span></div>
                )}
                {(taxDetailsSplit.igstAdded > 0 || taxDetailsSplit.igstIncluded > 0) && (
                  <>
                    {(taxDetailsSplit.igstIncluded === 0 || taxDetailsSplit.igstAdded > 0) && (
                      <div style={styles.summaryRow}><span>IGST</span><span>{formatCurrency(taxDetailsSplit.igstAdded)}</span></div>
                    )}
                    {taxDetailsSplit.igstIncluded > 0 && (
                      <div style={styles.summaryRow}><span>IGST (Included)</span><span>{formatCurrency(taxDetailsSplit.igstIncluded)}</span></div>
                    )}
                  </>
                )}
              </>
            )}

            <div style={styles.summaryRow}>
              <div style={styles.checkboxRow}>
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} />
                <span>{t(LangKeys.add_bill_round_off)}</span>
              </div>
              <span>{formatCurrency(roundOffAmount)}</span>
            </div>

            <div style={{ margin: '12px 0' }}><JaggedDivider /></div>

            <div style={{ ...styles.summaryRow, fontSize: '18px', fontWeight: '800' }}>
              <span>{t(LangKeys.add_bill_bill_amount)}</span>
              <span>{formatCurrency(finalAmount)}</span>
            </div>
          </div>
        </div>

        {/* OPTIONAL FIELDS */}
        <div style={styles.sectionCard}>
          <div style={styles.expandHeader} onClick={() => setShowOptional(!showOptional)}>
            <div style={styles.expandTitle}>
              <span style={{ fontWeight: '700' }}>{t(LangKeys.add_bill_optional_fields)}</span>
              <span style={styles.expandSub}>{t(LangKeys.add_bill_printed_on_invoice)}</span>
            </div>
            {showOptional ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {showOptional && (
            <div style={styles.expandContent}>
              <div style={styles.optRow} onClick={() => setActiveModal('custom_fields')}>
                <FileText size={18} color="#64748B" />
                <div style={styles.optInfo}>
                  <span>{t(LangKeys.add_bill_custom_fields)}</span>
                  {customFields.length > 0 && (
                    <span style={styles.optSummary}>{customFields.length} {t(LangKeys.fields || 'fields')}</span>
                  )}
                </div>
                <ChevronRight size={14} color="#CBD5E1" />
              </div>
              <div style={styles.optRow} onClick={() => setActiveModal('party_address')}>
                <MapPin size={18} color="#64748B" />
                <div style={styles.optInfo}>
                  <span>{t(LangKeys.add_bill_party_address)}</span>
                  {partyAddress && (
                    <span style={styles.optSummary}>{partyAddress.city}, {partyAddress.state}</span>
                  )}
                </div>
                <ChevronRight size={14} color="#CBD5E1" />
              </div>
              <div style={styles.optRow} onClick={() => setActiveModal('your_address')}>
                <ShieldCheck size={18} color="#64748B" />
                <div style={styles.optInfo}>
                  <span>{t(LangKeys.add_bill_your_address)}</span>
                  {yourAddress && (
                    <span style={styles.optSummary}>{yourAddress.city}, {yourAddress.state}</span>
                  )}
                </div>
                <ChevronRight size={14} color="#CBD5E1" />
              </div>
              <div style={styles.optRow} onClick={() => setActiveModal('terms')}>
                <StickyNote size={18} color="#64748B" />
                <div style={styles.optInfo}>
                  <span>{t(LangKeys.add_bill_terms_conditions)}</span>
                  {terms && (
                    <span style={styles.optSummary}>{terms.substring(0, 30)}...</span>
                  )}
                </div>
                <ChevronRight size={14} color="#CBD5E1" />
              </div>
            </div>
          )}
        </div>

        {/* PRIVATE NOTES */}
        <div style={styles.sectionCard}>
          <span style={styles.label}>{t(LangKeys.add_bill_private_to_you)}</span>
          <div style={styles.notesInput} onClick={() => setActiveModal('notes')}>
            <input
              placeholder={t(LangKeys.add_bill_add_notes_photos)}
              value={(() => {
                if (notes && photos.length > 0) {
                  return `${notes} (${photos.length} photos)`;
                }
                if (notes) return notes;
                if (photos.length > 0) {
                  return `${photos.length} photos`;
                }
                return '';
              })()}
              readOnly
              style={{ ...styles.rawInput, cursor: 'pointer' }}
            />
            <button style={styles.addNoteBtn}><Plus size={18} /></button>
          </div>
        </div>

        {/* PAYMENT DETAILS */}
        <div style={styles.sectionCard}>
          <div style={styles.chipGroup}>
            {['unpaid', 'cash', 'online'].map(p => (
              <button
                key={p}
                style={{ ...styles.chip, ...(paymentType === p ? styles.chipActive : {}) }}
                onClick={() => setPaymentType(p)}
              >
                {paymentType === p && <Check size={14} />}
                <span>{p === 'unpaid' ? t(LangKeys.bills_status_unpaid) : p === 'cash' ? t(LangKeys.cash) : t(LangKeys.online)}</span>
              </button>
            ))}
          </div>

          {paymentType === 'unpaid' && (
            <div style={styles.ledgerCheck}>
              <input type="checkbox" checked={addToLedger} onChange={(e) => setAddToLedger(e.target.checked)} />
              <span>{t(LangKeys.add_bill_add_due_to_ledger)}</span>
            </div>
          )}

          <div style={{ ...styles.summaryRow, marginTop: '16px', fontWeight: '700' }}>
            <span>{t(LangKeys.add_bill_balance_due)}</span>
            <span style={{ color: balanceDue > 0 ? '#EF4444' : '#10B981' }}>{formatCurrency(balanceDue)}</span>
          </div>
        </div>
      </div>

      <div style={styles.bottomBar}>
        <button
          style={{ ...styles.generateBtn, opacity: isSaving ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : getGenerateButtonText()}
        </button>
      </div>

      {/* MODAL OVERLAYS */}
      {activeModal === 'parties' && (
        <PartySelectModal
          isOpen={true}
          partyType={type === 'purchase' ? 'supplier' : 'customer'}
          onSelect={(party) => {
            setSelectedCustomer(party);
            if (party.address) setPartyAddress(party.address);
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'items' && (
        <ItemSelectModal
          isOpen={true}
          billType={type}
          isGstBill={isGst}
          initialSelectedItems={billItems}
          onSelect={(items) => { setBillItems(items); setActiveModal(null); }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'quick_add' && (
        <QuickAddItemModal
          isOpen={true}
          onSave={(newItem) => {
            setBillItems([...billItems, newItem]);
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'custom_fields' && (
        <CustomFieldsModal
          isOpen={true}
          initialFields={customFields}
          onSave={(fields) => { setCustomFields(fields); setActiveModal(null); }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'party_address' && (
        <PartyAddressModal
          isOpen={true}
          initialAddress={partyAddress}
          onSave={(addr) => { setPartyAddress(addr); setActiveModal(null); }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'your_address' && (
        <YourAddressModal
          isOpen={true}
          initialAddress={yourAddress}
          onSave={(addr) => { setYourAddress(addr); setActiveModal(null); }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'terms' && (
        <TermsConditionsModal
          isOpen={true}
          initialText={terms}
          onSave={(t) => { setTerms(t); setActiveModal(null); }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'notes' && (
        <AddNotesModal
          isOpen={true}
          initialNotes={notes}
          initialPhotos={photos}
          onSave={(n) => {
            setNotes(n.notes);
            setPhotos(n.photos);
            setActiveModal(null);
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
      {editingItemIndex !== null && (
        <EditPriceModal
          isOpen={true}
          billItem={billItems[editingItemIndex]}
          isGstBill={isGst}
          isPurchaseBill={type === 'purchase'}
          onSave={handleSavePrice}
          onClose={() => setEditingItemIndex(null)}
        />
      )}
      {activeModal === 'addParty' && (
        <AddPartyModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialType={type === 'purchase' ? 'supplier' : 'customer'}
          onSuccess={(newParty) => {
            setActiveModal(null);
            if (newParty && newParty.id) {
              setSelectedCustomer({
                id: newParty.id,
                customerName: newParty.name,
                mobileNo: newParty.mobile,
                gstin: newParty.gstin,
                address: newParty.address,
                isSupplier: newParty.is_supplier === 1 || newParty.is_supplier === '1'
              });
              if (newParty.address) setPartyAddress(newParty.address);
            }
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F8FAFC' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', background: 'white' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  scrollContent: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' },
  sectionCard: { background: 'white', borderRadius: '16px', padding: '16px', border: '1px solid #E2E8F0' },
  row: { display: 'flex', gap: '16px' },
  inputGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' },
  inputWrapper: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0', height: '40px' },
  rawInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', fontWeight: '700', width: '100%' },
  valText: { fontSize: '14px', fontWeight: '700', color: '#0F172A' },
  searchButton: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#F1F5F9', borderRadius: '12px', border: 'none', width: '100%', color: '#64748B', fontWeight: '600', marginTop: '8px' },
  selectedParty: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' },
  partyIcon: { width: '40px', height: '40px', background: '#F1F5F9', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  partyInfo: { flex: 1, display: 'flex', flexDirection: 'column' },
  partyName: { fontSize: '15px', fontWeight: '800', color: '#0F172A' },
  partySub: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  removeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' },
  textActionBtn: { border: 'none', background: 'transparent', color: '#1D4ED8', fontWeight: '800', fontSize: '13px', padding: '8px 0', cursor: 'pointer', textAlign: 'left' },
  itemsList: { marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  emptyItems: { padding: '24px', border: '2px dashed #E2E8F0', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#94A3B8', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  itemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#F8FAFC', borderRadius: '12px' },
  itemMeta: { display: 'flex', flexDirection: 'column', gap: '2px' },
  itemName: { fontSize: '14px', fontWeight: '700', color: '#0F172A' },
  itemPrice: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  itemRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  itemTotal: { fontSize: '14px', fontWeight: '800', color: '#0F172A' },
  itemDelBtn: { border: 'none', background: 'transparent', color: '#EF4444', cursor: 'pointer' },
  itemActions: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' },
  outlineBtn: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #1D4ED8', background: 'white', color: '#1D4ED8', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  summaryList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  jaggedContainer: { width: '100%', height: '10px', margin: '4px 0' },
  expandHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  expandTitle: { display: 'flex', flexDirection: 'column' },
  expandSub: { fontSize: '11px', color: '#94A3B8' },
  expandContent: { marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #F1F5F9', paddingTop: '16px' },
  optRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #F8FAFC', cursor: 'pointer' },
  optInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '14px', fontWeight: '600', color: '#475569' },
  optSummary: { fontSize: '12px', color: '#94A3B8', fontWeight: '500' },
  notesInput: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' },
  addNoteBtn: { width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  chipGroup: { display: 'flex', gap: '8px' },
  chip: { flex: 1, height: '36px', borderRadius: '18px', border: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: '#475569', cursor: 'pointer' },
  chipActive: { background: '#1D4ED8', borderColor: '#1D4ED8', color: 'white' },
  ledgerCheck: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '13px', fontWeight: '600', color: '#475569' },
  bottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E2E8F0' },
  generateBtn: { width: '100%', height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', fontSize: '15px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(29, 78, 216, 0.2)' },
};

export default AddBillView;
